/**
 * Commercial Pipeline API
 *
 * Strategy → TodayDecision → ExecutionPlan → MaterialPlanner → Workflow
 * （接线既有 Draft，不新增 Brain）
 */
import { supabase } from '@/lib/supabase';
import { getLatestWeeklyBoard, getTasks, type DbWeeklyBoard } from '@/api/astraea';
import {
  buildOperationalSnapshot,
  priorDates,
  shanghaiDateKey,
  buildTodayDecision,
  buildExecutionPlan,
  buildMaterialPlan,
  materialPlanToPackItems,
  materialItemKey,
  materialItemLabel,
  materialItemBilling,
  runWorkflowQueue,
  conversionPlaybookFromDecision,
  type TodayDecision,
  type ExecutionPlan,
  type MaterialPlan,
  type MaterialPlanItem,
  type StrategyContext,
  type WorkflowCallbacks,
  type ConversionPlaybook,
} from '@/lib/commercial';

export interface CommercialPipeline {
  decision: TodayDecision;
  executionPlan: ExecutionPlan;
  materialPlan: MaterialPlan;
  playbook: ConversionPlaybook;
}

export interface MaterialSuggestRow {
  key: string;
  label: string;
  reason: string;
  usage: string;
  count: number;
  action: string;
  units: number;
  points: number;
  eta: string;
  item: MaterialPlanItem;
}

async function fetchRecentAssets(characterId: number) {
  const since = new Date();
  since.setDate(since.getDate() - 8);
  const { data, error } = await supabase
    .from('assets')
    .select('created_at, material_type, asset_type, meta')
    .eq('character_id', characterId)
    .gte('created_at', since.toISOString())
    .order('created_at', { ascending: false });
  if (error) return [];
  return (data ?? []).map((a) => ({
    created_at: String(a.created_at).slice(0, 10),
    material_type: (a.meta as Record<string, unknown> | null)?.material_type as string | null
      ?? (a.asset_type === 'image' ? 'photo' : a.asset_type === 'video' ? 'silent_video' : null),
    photo_subtype: (a.meta as Record<string, unknown> | null)?.photo_subtype as string | null,
    asset_type: a.asset_type as string,
  }));
}

function buildStrategyContext(
  board: DbWeeklyBoard | null,
  todayDayIndex: number,
): StrategyContext {
  return {
    boardId: board?.id ?? null,
    dayIndex: todayDayIndex,
    weeklyGoal: board?.weekly_goal ?? null,
    doNotThisWeek: board?.business_strategy?.do_not_this_week ?? [],
    contentRhythm: (board?.content_rhythm as Record<string, string>) ?? {},
    todayTaskPlatforms: [],
  };
}

async function resolveTodayDayIndex(board: DbWeeklyBoard | null): Promise<number> {
  if (!board?.id) return 1;
  const tasks = await getTasks(board.id).catch(() => []);
  const indices = tasks
    .map((t) => t.day_index)
    .filter((d): d is number => typeof d === 'number' && d >= 1 && d <= 7);
  if (indices.length === 0) return Math.min(7, Math.max(1, board.phase ?? 1));
  return Math.min(...indices);
}

/** 构建完整商业管线（不执行 workflow） */
export async function buildCommercialPipeline(
  characterId: number,
  characterPublic?: { name?: string; description?: string; tone?: string; speaking_style?: string },
): Promise<CommercialPipeline> {
  const decisionDate = shanghaiDateKey();
  const [board, assets] = await Promise.all([
    getLatestWeeklyBoard(characterId).catch(() => null),
    fetchRecentAssets(characterId),
  ]);

  const memory = buildOperationalSnapshot(
    characterId,
    decisionDate,
    priorDates(7, decisionDate),
    assets,
  );

  const strategy = buildStrategyContext(board, await resolveTodayDayIndex(board));
  const decision = buildTodayDecision({ characterId, decisionDate, strategy, memory });
  const executionPlan = buildExecutionPlan(decision);
  const materialPlan = buildMaterialPlan(decision, executionPlan.queue);
  const playbook = conversionPlaybookFromDecision(decision, characterPublic);

  return { decision, executionPlan, materialPlan, playbook };
}

/** 仅获取今日决策（UI 展示用） */
export async function getTodayDecision(characterId: number): Promise<TodayDecision> {
  const pipeline = await buildCommercialPipeline(characterId);
  return pipeline.decision;
}

/** 把 MaterialPlan 转成可勾选清单（含点数与耗时） */
export function buildMaterialSuggestRows(
  plan: MaterialPlan,
  costMap: Record<string, number>,
): MaterialSuggestRow[] {
  return plan.items.map((item) => {
    const billing = materialItemBilling(item);
    const unit = costMap[billing.action] ?? 0;
    return {
      key: materialItemKey(item),
      label: materialItemLabel(item),
      reason: item.reason || '',
      usage: item.usage || '',
      count: item.count || 1,
      action: billing.action,
      units: billing.units,
      points: unit * billing.units,
      eta: billing.eta,
      item,
    };
  });
}

/** 执行勾选的素材（确认点数后由 UI 调用） */
export async function executeSelectedMaterials(
  characterId: number,
  packId: string,
  selectedKeys: string[],
  callbacks?: WorkflowCallbacks,
): Promise<{ ok: boolean; message: string; decision: TodayDecision }> {
  const { decision, executionPlan, materialPlan } = await buildCommercialPipeline(characterId);
  const result = await runWorkflowQueue(
    characterId,
    materialPlan,
    executionPlan,
    packId,
    callbacks,
    selectedKeys,
  );
  return {
    ok: result.ok,
    message: result.message,
    decision,
  };
}

/** 执行今日素材包：走完整管线（兼容旧「一键」入口） */
export async function executeDailyMaterialPipeline(
  characterId: number,
  packId: string,
): Promise<{ ok: boolean; message: string; decision: TodayDecision }> {
  const { decision, executionPlan, materialPlan } = await buildCommercialPipeline(characterId);
  const result = await runWorkflowQueue(characterId, materialPlan, executionPlan, packId);
  return {
    ok: result.ok,
    message: result.message,
    decision,
  };
}

export { materialPlanToPackItems, materialItemKey, materialItemLabel, materialItemBilling };
